import * as Sentry from '@sentry/react';
import { UserType, useUserHasFeature, useUserSession } from '@solinkcorp/authentication';
import settings from '@solinkcorp/settings';
import { ComponentLoadingSpinner } from '@solinkcorp/solink-components';
import Loadable from 'components/Loadable';
import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { isKiosk } from 'src/core/utils/kioskUtils';
import { env, IS_PRODUCTION } from 'src/environment';
import useLocale from 'src/hooks/useLocale';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import FeaturesUtil from 'utils/FeaturesUtil';
import { DASHBOARD_URL, EVENTS_URL, SETTINGS_URL_PREFIX, VIDEO_URL_PREFIX } from 'utils/urls';
import * as constants from 'vd-constants';
import { SETTINGS_PAGE_URL } from 'vd-constants';
import pkg from '../../package.json';
import ConfigMiddleware from './ConfigMiddleware/ConfigMiddleware';
import { type NavTabWithComponent } from './NavigationFrame/menu';
import PageNotFound from './PageNotFound/PageNotFound';
import PrivateRoute from './PrivateRoute';
import useCanManageSolinkApiKeys from './SettingsPage/DeveloperPage/useCanManageSolinkApiKeys';
// Lazy load settings pages to reduce main bundle size
const AppsPage = Loadable({ loader: () => import('./SettingsPage/AppsPage') });
const CloudStoragePage = Loadable({
  loader: () => import('./SettingsPage/CloudStorage/CloudStoragePage/CloudStoragePage'),
});
const CloudVideoBackupPage = Loadable({
  loader: () => import('./SettingsPage/CloudStorage/CloudVideoBackupPage/CloudVideoBackupPage'),
});
const EventFlowLandingView = Loadable({
  loader: () => import('./SettingsPage/EventFlowPage/EventFlowLandingView/EventFlowLandingView'),
});
const EventFlowPage = Loadable({
  loader: () => import('./SettingsPage/EventFlowPage/EventFlowPage'),
});
const WorkflowFormView = Loadable({
  loader: () => import('./SettingsPage/EventFlowPage/WorkflowFormView'),
});
const LanguagesPage = Loadable({ loader: () => import('./SettingsPage/LanguagesPage') });
const LocationDetailsPage = Loadable({
  loader: () => import('./SettingsPage/LocationDetailsPage/LocationDetailsPage'),
});
const LocationManagementPage = Loadable({
  loader: () => import('./SettingsPage/LocationManagementPage'),
});
const MyAccountPage = Loadable({ loader: () => import('./SettingsPage/MyAccountPage') });
const NotificationsPage = Loadable({
  loader: () => import('./SettingsPage/NotificationsPage/NotificationsPage'),
});
const OverridesPage = Loadable({ loader: () => import('./SettingsPage/OverridesPage') });
const PreferencesPage = Loadable({ loader: () => import('./SettingsPage/PreferencesPage') });
const RecordingsPage = Loadable({ loader: () => import('./SettingsPage/RecordingsPage') });
const UserRolesPage = Loadable({
  loader: () => import('./SettingsPage/UserRolesPage/UserRolesPage'),
});
const UsersPage = Loadable({ loader: () => import('./SettingsPage/UsersPage/UsersPage') });
const DeveloperPage = Loadable({
  loader: () => import('./SettingsPage/DeveloperPage/DeveloperPage'),
});
const SidekickPage = Loadable({ loader: () => import('./SidekickPage/SidekickPage') });

const { version } = pkg;

const IS_KIOSK = isKiosk();

if (IS_PRODUCTION) {
  Sentry.init({
    dsn: 'https://053c44b670dc4294b9b436a6c676ed06@o488136.ingest.sentry.io/5547950',
    integrations: [new Sentry.BrowserTracing()],

    // Only send 1% of the events to Sentry to reduce noise for non-kiosk mode
    // and avoid hitting the Quota limit.
    // For kiosk mode, send all events to Sentry to help with debugging
    // because there are no logs to check on the device.
    tracesSampleRate: IS_KIOSK ? 1 : 0.01,
    environment: env.PROD ? 'production' : env.MODE,
    release: version,
    beforeSend: async (event) => {
      // scrub sensitive data
      event.breadcrumbs
        ?.filter((b) => b.category === 'fetch')
        .forEach((req) => {
          if (req.data) {
            req.data.url = req.data.url.replace(/jwt=(\S+)/, 'jwt=[hidden]');
          }
        });

      // Modify the event here
      return event;
    },
  });
}

const EventPlayerRoute = Loadable({
  loader: () => import('./EventPlayer/EventPlayerRoute'),
  loading: ComponentLoadingSpinner,
});

const EventsPage = Loadable({
  loader: () => import('./EventsPage/EventsPage'),
  loading: ComponentLoadingSpinner,
});

const SettingsPage = Loadable({
  loader: () => import('./SettingsPage/SettingsPage'),
});

const GuestAccessExpired = Loadable({
  loader: () => import('./GuestAccessExpired'),
});

const AccessRestricted = Loadable({
  loader: () => import('./AccessRestricted/AccessRestricted'),
});

export default function AppRoutes(props: {
  userPermittedRoutes: NavTabWithComponent[];
  defaultPath: string;
}) {
  const locale = useLocale();
  const user = useUserSession();
  const featureStandard = useFeatureStandard();
  const location = useLocation();

  const isAdminUser = user.profile.userType === UserType.ADMIN;
  const isSolinkUser = user.isSolinkUser;
  const isBetaApiAllowed = !!settings.beta_client || !!settings.beta_events;
  const settingsRedirectElement = <Navigate to={SETTINGS_URL_PREFIX} />;

  const shouldShowOverridesPage = isAdminUser && isSolinkUser && isBetaApiAllowed;

  const canAccessAlarmStatusOnly = useUserHasFeature('alarmAccessOnly');
  // eslint-disable-next-line
  // @ts-ignore: user feature not yet defined in user feature standard
  const hasAccessToSidekickPage = useUserHasFeature('videoSearch');
  const canAccessDeveloperPage = useCanManageSolinkApiKeys();

  useEffect(() => {
    if (user && IS_PRODUCTION) {
      Sentry.setUser({
        id: user.userId,
        email: user.profile.email,
        userType: user.profile.userType,
      });

      Sentry.setTag('kiosk_mode', IS_KIOSK ? 'true' : 'false');
    }
  }, [user]);

  return (
    <Routes>
      <Route path="/guest-access-expired" element={<GuestAccessExpired />} />
      <Route
        path="/web-access-restricted"
        element={
          <AccessRestricted
            message={locale.webAccessRestricted}
            redirectButtonText={locale.general.gotit}
          />
        }
      />
      {FeaturesUtil.isLiveViewOnly() && (
        <Route path="/events/:feature/:eventId" element={<Navigate to={VIDEO_URL_PREFIX} />} />
      )}
      {FeaturesUtil.canViewEventsPage() && !canAccessAlarmStatusOnly && (
        <Route
          path="/events/all"
          element={
            <PrivateRoute contextAction={constants.EVENTS_PAGE}>
              <EventsPage />
            </PrivateRoute>
          }
        />
      )}
      {FeaturesUtil.canViewEventsPage() &&
        featureStandard &&
        Object.keys(featureStandard.tabs).map((feature) => {
          if (
            !FeaturesUtil.canViewFeatureStandardTab(
              featureStandard.tabs[feature]?.visibilityRestrictions || {},
            )
          ) {
            return (
              <Route
                key={`feature-${feature}`}
                path={`/events/${feature}`}
                element={<Navigate to={EVENTS_URL} />}
              />
            );
          }

          return (
            <React.Fragment key={`feature-${feature}`}>
              <Route
                key={`feature-${feature}`}
                path={`/events/${feature}`}
                element={
                  <PrivateRoute>
                    <EventsPage />
                  </PrivateRoute>
                }
              />
              <Route
                key={`event-feature-${feature}`}
                path={`/events/${feature}/:eventId`}
                element={
                  FeaturesUtil.isLiveViewOnly() ? (
                    <Navigate to={VIDEO_URL_PREFIX} />
                  ) : (
                    <PrivateRoute>
                      <EventPlayerRoute />
                    </PrivateRoute>
                  )
                }
              />
            </React.Fragment>
          );
        })}
      <Route
        path="/events/all/:eventId"
        element={
          FeaturesUtil.isLiveViewOnly() ? (
            <Navigate to={VIDEO_URL_PREFIX} />
          ) : (
            FeaturesUtil.canViewEventsPage() && (
              <PrivateRoute contextAction={constants.EVENTS_PAGE}>
                <EventPlayerRoute />
              </PrivateRoute>
            )
          )
        }
      />
      <Route
        path="/events/:eventId"
        element={
          FeaturesUtil.isLiveViewOnly() ? (
            <Navigate to={VIDEO_URL_PREFIX} />
          ) : (
            FeaturesUtil.canViewEventsPage() && (
              <PrivateRoute contextAction={constants.EVENTS_PAGE}>
                <EventPlayerRoute />
              </PrivateRoute>
            )
          )
        }
      />
      {props.userPermittedRoutes.map((route) => (
        <Route
          key={route.page}
          path={`${route.route}/*`}
          element={
            <PrivateRoute contextAction={route.page}>
              <route.component />
            </PrivateRoute>
          }
        />
      ))}

      <Route
        path={SETTINGS_URL_PREFIX}
        element={
          <PrivateRoute contextAction={constants.SETTINGS_PAGE}>
            <SettingsPage
              shouldShowOverridesPage={shouldShowOverridesPage}
              shouldShowDeveloperPage={canAccessDeveloperPage}
              userSession={user}
            />
          </PrivateRoute>
        }
      >
        <Route key="settings-home" index element={<MyAccountPage />} />
        <Route
          key="settings-account"
          path={SETTINGS_PAGE_URL.ACCOUNT}
          element={<MyAccountPage />}
        />
        <Route key="settings-lang" path={SETTINGS_PAGE_URL.LANGUAGES} element={<LanguagesPage />} />
        <Route
          key="settings-notifications"
          path={SETTINGS_PAGE_URL.NOTIFICATIONS}
          element={canAccessAlarmStatusOnly ? settingsRedirectElement : <NotificationsPage />}
        />
        <Route
          key="settings-pref"
          path={SETTINGS_PAGE_URL.PREFERENCES}
          element={<PreferencesPage />}
        />

        {/* Solink user routes  */}
        <Route
          key="settings-rec"
          path={SETTINGS_PAGE_URL.RECORDINGS}
          element={isAdminUser && isSolinkUser ? <RecordingsPage /> : settingsRedirectElement}
        />
        <Route
          key="settings-overrides"
          path={SETTINGS_PAGE_URL.OVERRIDES}
          element={shouldShowOverridesPage ? <OverridesPage /> : settingsRedirectElement}
        />

        {/* Admin user routes */}
        <Route
          key="settings-apps"
          path={SETTINGS_PAGE_URL.APPS}
          element={isAdminUser ? <AppsPage /> : settingsRedirectElement}
        />
        <Route
          key="settings-user-roles"
          path={SETTINGS_PAGE_URL.USER_ROLES}
          element={
            isAdminUser && !canAccessAlarmStatusOnly ? <UserRolesPage /> : settingsRedirectElement
          }
        />
        <Route
          key="settings-users"
          path={SETTINGS_PAGE_URL.USERS}
          element={
            isAdminUser && !canAccessAlarmStatusOnly ? <UsersPage /> : settingsRedirectElement
          }
        />
        <Route
          key="settings-location-management"
          path={SETTINGS_PAGE_URL.LOCATION_MANAGEMENT}
          element={
            isAdminUser && !canAccessAlarmStatusOnly ? (
              <LocationManagementPage />
            ) : (
              settingsRedirectElement
            )
          }
        />
        <Route
          key="settings-location-details"
          path={`${SETTINGS_PAGE_URL.LOCATION_DETAILS}/:id`}
          element={
            isAdminUser && !canAccessAlarmStatusOnly ? (
              <LocationDetailsPage />
            ) : (
              settingsRedirectElement
            )
          }
        />
        <Route
          key="settings-cloud-storage"
          path={SETTINGS_PAGE_URL.CLOUD_STORAGE}
          element={
            FeaturesUtil.cloudVideoStorage() ? <CloudStoragePage /> : settingsRedirectElement
          }
        />
        <Route
          key="settings-cloud-video-backup"
          path={SETTINGS_PAGE_URL.CLOUD_VIDEO_BACKUP}
          element={
            FeaturesUtil.cloudVideoStorage() ? <CloudVideoBackupPage /> : settingsRedirectElement
          }
        />
        {/* Legacy route that was used in digest emails */}
        {/* Route `/settings/reports` was renamed to `/settings/notifications` */}
        <Route
          key="settings-reports"
          path={SETTINGS_PAGE_URL.LEGACY_REPORTS}
          element={
            <Navigate
              to={{
                ...location, // keep the same `search` param to track external link source
                pathname: `${SETTINGS_URL_PREFIX}/${SETTINGS_PAGE_URL.NOTIFICATIONS}`,
              }}
            />
          }
        />
        <Route
          key="settings-developer"
          path={SETTINGS_PAGE_URL.DEVELOPER}
          element={canAccessDeveloperPage ? <DeveloperPage /> : settingsRedirectElement}
        />
        <Route
          key="settings-event-flow"
          path={SETTINGS_PAGE_URL.EVENT_FLOW}
          element={FeaturesUtil.canManageEventFlows() ? <EventFlowPage /> : settingsRedirectElement}
        >
          <Route index element={<EventFlowLandingView />} />
          <Route path="new" element={<WorkflowFormView mode="create" />} />
          <Route path=":workflowId" element={<WorkflowFormView mode="edit" />} />
        </Route>
      </Route>
      <Route
        path="/start-qnap-kiosk"
        element={<ConfigMiddleware defaultPath={props.defaultPath} />}
      />
      <Route
        path="/start-with-config"
        element={<ConfigMiddleware defaultPath={props.defaultPath} />}
      />
      {hasAccessToSidekickPage && <Route path="/sidekick" element={<SidekickPage />} />}
      {/* Legacy routes */}
      <Route path="/home" element={<Navigate to={DASHBOARD_URL} />} />
      <Route path="/not-found" element={<PageNotFound />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
}
